import { useState, useEffect } from 'react';
import axios from 'axios';
import { Ghost, Trash2, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

import { API_BASE as API } from '../../config';

/**
 * Orphaned-company cleanup for the Dev Hub.
 *
 * An "orphan" is a `companies` doc whose `tenant_id` is missing, null, or
 * the empty string. Such docs don't appear in any provider's normal CRUD
 * list, can't be deleted from the Organizations page, and tend to be the
 * leftovers of buggy seed scripts or migrations. This page enumerates
 * them and lets a developer hard-delete each one (with a confirmation
 * step that surfaces dependent claim count so nothing is removed
 * silently).
 */
export default function DevOrphans() {
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchOrphans = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dev/orphans/companies`);
      setOrphans(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load orphan companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrphans(); }, []);

  const handleDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${API}/dev/orphans/companies/${encodeURIComponent(confirmTarget.name)}`
      );
      toast.success(`Removed ${res.data.deleted_count} orphan doc(s) named "${confirmTarget.name}"`);
      setConfirmTarget(null);
      await fetchOrphans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 text-slate-200" data-testid="dev-orphans-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Ghost className="w-5 h-5 text-emerald-400" />
            Orphan Companies
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Company records with no <code className="text-emerald-400">tenant_id</code> — leftovers
            from old seed scripts. They don't show on any tenant's screens, so the
            normal delete flow can't reach them. Hard-delete them here.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchOrphans}
          disabled={loading}
          className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
          data-testid="dev-orphans-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : orphans.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-8 text-center">
          <Ghost className="w-8 h-8 mx-auto text-slate-600 mb-2" />
          <p className="text-sm text-slate-400">No orphan companies. Database is clean.</p>
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-slate-400 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Company Name</th>
                <th className="text-left px-4 py-2.5 font-medium">DOT #</th>
                <th className="text-left px-4 py-2.5 font-medium">tenant_id</th>
                <th className="text-left px-4 py-2.5 font-medium">org_id</th>
                <th className="text-right px-4 py-2.5 font-medium">Claims</th>
                <th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {orphans.map((c) => (
                <tr
                  key={c.name}
                  className="border-t border-slate-800 hover:bg-slate-800/40"
                  data-testid={`orphan-row-${c.name}`}
                >
                  <td className="px-4 py-2.5 text-slate-100 font-medium">{c.name}</td>
                  <td className="px-4 py-2.5 text-slate-400">{c.dot_number || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">
                    {c.tenant_id === null ? <span className="text-rose-400">null</span>
                      : c.tenant_id === '' ? <span className="text-rose-400">""</span>
                      : c.tenant_id === undefined ? <span className="text-rose-400">missing</span>
                      : c.tenant_id}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">
                    {c.org_id || <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {c.claim_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-amber-400 text-xs">
                        <AlertCircle className="w-3 h-3" />
                        {c.claim_count}
                      </span>
                    ) : (
                      <span className="text-slate-600">0</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmTarget(c)}
                      className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 h-7 px-2"
                      data-testid={`orphan-delete-${c.name}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!confirmTarget} onOpenChange={(open) => !open && setConfirmTarget(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-rose-400" />
              Hard-delete orphan company
            </DialogTitle>
          </DialogHeader>
          {confirmTarget && (
            <div className="text-sm space-y-3">
              <p>
                Permanently remove the orphan record{' '}
                <span className="font-semibold text-white">"{confirmTarget.name}"</span>?
              </p>
              {confirmTarget.claim_count > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-amber-300 text-xs flex gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    {confirmTarget.claim_count} claim(s) reference this name. They will <b>not</b>{' '}
                    be deleted, but their <code>company_name</code> link will point to a record
                    that no longer exists.
                  </span>
                </div>
              )}
              <p className="text-xs text-slate-500">
                Only docs matching <code>tenant_id ∈ &#123;null, "", missing&#125;</code> are
                affected — real tenant companies are never touched.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmTarget(null)}
              disabled={deleting}
              className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
              data-testid="orphan-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="orphan-confirm-delete"
            >
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
